<template>
    <ModalDialogContainer ref="modalContainer" v-model:display="displayStatus" @keydown="escapeToClose">
        <div class="modal-dialog modal-md" role="document" @click="stopPropagationEvent">
            <div class="modal-content">
                <div class="modal-header">
                    <div class="modal-title">{{ $t("Share call invitation") }}</div>
                    <button type="button" class="btn-close" :title="$t('Close')" @click="close"></button>
                </div>
                <div class="modal-body">
                    <!-- Call link (read-only + copy) -->
                    <label class="form-label bold-class">{{ $t("Call link") }}</label>
                    <div class="input-group mb-3">
                        <input type="text" class="form-control" :value="callLink" readonly />
                        <button class="btn btn-secondary" type="button" @click="copyLink" :title="$t('Copy link')">
                            <i class="fa-regular fa-copy"></i>
                        </button>
                    </div>

                    <!-- Email input -->
                    <label class="form-label bold-class">{{ $t("Email address") }}</label>
                    <input
                        ref="emailInput"
                        v-model="email"
                        type="email"
                        class="form-control"
                        :placeholder="$t('Enter recipient email')"
                        :class="{ 'is-invalid': emailError }"
                        @keydown.enter="sendInvitation"
                    />
                    <div v-if="emailError" class="invalid-feedback">{{ emailError }}</div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-primary" @click="sendInvitation" :disabled="sending">
                        <i class="fas fa-paper-plane"></i> {{ $t("Send invitation") }}
                    </button>
                    <button type="button" class="btn btn-cancel" @click="close">
                        {{ $t("Cancel") }}
                    </button>
                </div>
            </div>
        </div>
    </ModalDialogContainer>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useVModel } from "@/utils/v-model";
import { Request } from "@asanrom/request-browser";
import { ApiMail } from "@/api/api-group-mail";

export default defineComponent({
    name: "ShareCallEmailModal",
    emits: ["update:display"],
    props: {
        display: Boolean,
        roomId: String,
        callLink: String,
    },
    setup(props) {
        return {
            displayStatus: useVModel(props, "display"),
        };
    },
    data() {
        return {
            email: "",
            emailError: "",
            sending: false,
        };
    },
    methods: {
        close() {
            this.displayStatus = false;
            this.email = "";
            this.emailError = "";
        },

        escapeToClose(event: KeyboardEvent) {
            if (event.key === "Escape") {
                this.close();
            }
        },

        stopPropagationEvent(e: Event) {
            e.stopPropagation();
        },

        copyLink() {
            if (this.callLink) {
                navigator.clipboard.writeText(this.callLink).catch(() => { /**/ });
                this.$showSnackBar(this.$t("Call link copied to clipboard"));
            }
        },

        sendInvitation() {
            // Validate email
            this.emailError = "";
            const trimmed = (this.email || "").trim();
            if (!trimmed) {
                this.emailError = this.$t("Please enter an email address");
                return;
            }
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(trimmed)) {
                this.emailError = this.$t("Please enter a valid email address");
                return;
            }

            this.sending = true;

            Request.Do(
                ApiMail.ShareCallByEmail({
                    email: trimmed,
                    link: this.callLink || "",
                    rid: this.roomId || "",
                }),
            )
                .onSuccess(() => {
                    this.$showSnackBar(this.$t("Invitation sent successfully"));
                    this.sending = false;
                    this.close();
                })
                .onRequestError((err) => {
                    this.sending = false;
                    this.$showSnackBar(this.$t("Failed to send invitation"));
                    console.error("ShareCallByEmail error", err);
                })
                .onUnexpectedError((err) => {
                    this.sending = false;
                    this.$showSnackBar(this.$t("Failed to send invitation"));
                    console.error("ShareCallByEmail unexpected error", err);
                });
        },
    },
    watch: {
        display() {
            if (this.display) {
                this.$nextTick(() => {
                    const input = this.$refs.emailInput as HTMLInputElement;
                    if (input) input.focus();
                });
            }
        },
    },
});
</script>
